// app/src/features/session/AuthShells.jsx
import React from "react";
import TosGate from "../../TosGate";
import { redirectToLogin } from "../../lib/apiClient.js";
import { C } from "../../lib/colors.js";

/**
 * The three pre-auth shells `AppInner` returned early with, in their original order (each gate
 * masks the next: a loading state never redirects; the Zitadel bounce wins over the TOS gate):
 * the bg-only loading shell while `authLoading`, the bg-only shell behind the render-time redirect
 * to Zitadel when auth is on and nobody is signed in, and the TOS gate for a signed-in user whose
 * acceptance is stale. Returns the shell element, or `null` when no gate fires and `AppInner`
 * renders the app. A render helper, not a component: `AppInner` calls it below its last hook and
 * returns whatever it returns, so the gate conditions live in this one place.
 */
export function renderAuthShell({ authLoading, authEnabled, me, tos, setTos, themeRaw }) {
  // Server-side requireAuth middleware is the real gate on /api/*; this client-side check is purely
  // for UX (avoids flashing the empty app shell to anonymous visitors).
  if (authLoading) {
    return <div style={{ width: "100vw", height: "100vh", background: C.bg }} />;
  }
  if (authEnabled && !me) {
    // Redirect straight to Zitadel — the IdP is the branded entry point, so
    // there's no point showing a local sign-in splash first. The bg-only
    // return null prevents a flash of the unstyled shell during the navigation.
    // `replace`: the blank pre-auth shell must not sit in history (Back from the IdP would only
    // redirect again); `redirectToLogin` builds the same `/auth/login?returnTo=<path+query>`.
    if (typeof window !== "undefined") redirectToLogin({ replace: true });
    return <div style={{ width: "100vw", height: "100vh", background: C.bg }} />;
  }
  if (me && tos && !tos.isCurrent) {
    // Key derived from the current manifest's name@version pairs so a manifest
    // bump (e.g. 409 version_mismatch, or visibilitychange-triggered refetch)
    // forces a full remount — clears local submitting/error state and re-runs
    // the focus-trap setup against the new doc list.
    const tosKey = tos.current.map((d) => `${d.name}@${d.version}`).join(",");
    // Pass the RAW-HEX palette (themeRaw = themeColors(theme)), not the var(--c-*)
    // map `C`: the gate renders before GlobalStyles mounts, so the `--c-*` CSS
    // variables don't exist yet and any var() reference would resolve to nothing.
    return (
      <TosGate key={tosKey} tos={tos} onAccepted={(newTos) => setTos(newTos)} colors={themeRaw} />
    );
  }
  return null;
}
